import {
  createApiHeaders,
  storeAuthToken,
  getStoredAuthToken,
} from "~/utils/api-headers";
import { getSiteActiveIdFromCookies } from "~/utils/cookieUtils";

// Define ReactNative context interface to match plugin implementation
interface ReactNativeContext {
  isApp: boolean;
  platform: string | null;
  appVersion: string | null;
  deviceId: string | null;
  authToken: string | null;
  debugMode?: boolean;
  allHeaders?: Record<string, string>;
  capabilities?: string[];
  theme?: string;
  hasCapability: (capability: string) => boolean;
  isVersionAtLeast: (version: string) => boolean;
}

export const useAuthService = () => {
  const config = useRuntimeConfig();
  const nuxtApp = useNuxtApp();

  // SECURITY: Removed direct references to sensitive backend URLs (2025-05-01)
  // These values should only be available on the server side via secure proxies

  const dayjs = useDayjs();

  // Access React Native context if available with proper typing
  const reactNative =
    typeof window !== "undefined"
      ? (nuxtApp.$reactNative as unknown as ReactNativeContext) || null
      : null;
  const signInBackend = async (
    username: string,
    password: string
  ): Promise<any> => {
    try {
      // Create headers with any existing auth token
      const headers = createApiHeaders();

      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs with credentials (2025-05-01)
      const response: any = await $fetch(`/api/auth/login`, {
        method: "POST",
        body: { username, password },
        credentials: "include",
        headers,
      });

      // If we received an auth token in the response and we're in a React Native context, store it
      if (response?.siteActiveId && reactNative?.isApp) {
        // Store the SITEACTIVEID as the auth token for React Native apps
        // This allows us to use it as X-GO-Auth-Token in future requests
        storeAuthToken(response.siteActiveId);
      }

      // Security: Error handling without logging for security (2025-05-05)
      return response; // Return full response
    } catch (error) {
      // Login attempt failed
      throw error;
    }
  };

  const validateEmail = async (email: string): Promise<any | null> => {
    try {
      const payload = {
        metadata: {
          request_time: dayjs().format(),
          ext_request_id: Date.now(),
        },
        request: {
          emailAddress: email,
        },
      };
      const endpoint = `/goPrepaid/emailCheckExisting`;
      // SECURITY: Use secure server-side proxy endpoints instead of direct backend URLs
      const response: any = await $fetch(
        `/api/integrate/goPrepaid${endpoint}`,
        {
          method: "POST",
          body: payload,
        }
      );
      // Security: Error handling without logging for security (2025-05-05)
      const data = response?.result || null;
      return data;
    } catch (error: any) {
      return false;
    }
  };

  /**
   * Check authentication status with the backend
   *
   * This function checks if the user is authenticated and has a valid 2FA session.
   * There are three possible outcomes:
   * 1. User has SITEACTIVEID cookie but no valid 2FA session (authRequired=true, authenticated=false)
   * 2. User has valid SITEACTIVEID and 2FA session (authenticated=true)
   * 3. User has no SITEACTIVEID cookie at all (returns null)
   *
   * @returns AuthStatusResponse object or null if no SITEACTIVEID cookie exists
   */
  /**
   * Check authentication status with the backend
   *
   * This function checks if the user is authenticated and has a valid 2FA session.
   *
   * CRITICAL FOR 2FA MODAL: Any time auth is required, we MUST throw a 401 Unauthorized error.
   * The UI code is specifically watching for these errors to trigger the 2FA modal.
   *
   * @returns AuthStatusResponse object for authenticated users, or throws 401 error for 2FA
   */
  const authStatus = async (transactionType?: string): Promise<any> => {
    try {
      // Check for SITEACTIVEID cookie - we need this for authentication
      // let siteActiveId = null;

      // if (typeof document !== "undefined") {
      //   const cookies = document.cookie.split(";");
      //   for (const cookie of cookies) {
      //     const [name, value] = cookie.trim().split("=");
      //     if (name === "SITEACTIVEID") {
      //       siteActiveId = value;
      //       break;
      //     }
      //   }
      // }

      // if (!siteActiveId || siteActiveId.trim() === "") {
      //   console.warn(
      //     " No SITEACTIVEID cookie found - cannot check auth status"
      //   );
      //   return {
      //     authenticated: false,
      //     authRequired: false,
      //     message: "No authentication session exists",
      //   };
      // }

      // Security: Error handling without logging for security (2025-05-05)

      // DIRECT APPROACH: Use fetch with proper error handling to ensure 401 status codes are properly detected
      // We need to avoid Nuxt's automatic error handling which can mask the status codes
      try {
        console.log("🔒 Checking auth status via direct fetch", {
          timestamp: new Date().toISOString(),
          url: "/api/integration/auth-status",
        });

        // Use raw fetch to get direct access to the response object and its status code
        let endpointUrl = "/api/integration/auth-status";
        if (transactionType) {
          endpointUrl += `?transactionType=${encodeURIComponent(transactionType)}`;
          console.log(`[AuthService] Checking authStatus with transactionType: ${transactionType} at URL: ${endpointUrl}`);
        } else {
          console.log(`[AuthService] Checking generic authStatus at URL: ${endpointUrl}`);
        }
        const originalResponse = await fetch(endpointUrl, {
          method: "GET",
          credentials: "include",
          cache: "no-store", // Ensure we don't get cached responses
        });

        // Log the raw response for debugging
        console.log("📡 Raw auth status response:", {
          status: originalResponse.status,
          statusText: originalResponse.statusText,
          headers: Object.fromEntries([...originalResponse.headers.entries()]),
        });

        // Get raw status code before processing
        const statusCode = originalResponse.status;

        // Read the response data
        const responseText = await originalResponse.text();
        let responseData = null;
        try {
          responseData = JSON.parse(responseText);
        } catch (e) {
          console.error("Failed to parse response as JSON:", responseText);
        }

        // CRITICAL: Check for 401 status and immediately throw a properly formed error
        if (statusCode === 401) {
          console.log("⚠️ Authentication required - status 401 detected");

          // Create a properly structured error with consistent format for error handlers
          const authError = new Error("Authentication required");
          (authError as any).status = 401;
          (authError as any).statusCode = 401;
          (authError as any).response = {
            status: 401,
            data: {
              error: { message: "Authentication required" },
              authRequired: true,
            },
          };

          // This error will be caught by the UI to trigger the 2FA flow
          throw authError;
        }

        // Also check for auth markers in the response body even if not 401
        const isAuthRequiredContent =
          responseData?.authRequired === true ||
          responseData?.error?.code === "AUTH_REQUIRED" ||
          responseData?.error?.errorCode === "AUTH_REQUIRED" ||
          responseData?.error?.errorMessage ===
            "authorization data not found." ||
          responseData?.message === "authorization data not found.";

        if (isAuthRequiredContent) {
          // Security: Error handling without logging for security (2025-05-05)

          // Create a properly structured error
          const authError = new Error("Authentication required");
          (authError as any).status = 401; // Force 401 status
          (authError as any).statusCode = 401;
          (authError as any).response = {
            status: 401,
            data: {
              error: { message: "Authentication required" },
              authRequired: true,
            },
          };

          throw authError;
        }

        // If we get here, user is authenticated
        // Security: Error handling without logging for security (2025-05-05)
        
        // For successful responses, check if authStatus is APPROVED
        // Only APPROVED status means the user is fully authenticated
        if (statusCode === 200 && responseData) {
          const authStatusValue = responseData.result?.body?.authStatus || 
                                 responseData.result?.authStatus || 
                                 responseData.authStatus;
          
          console.log("✅ Auth status check - status code 200, authStatus:", authStatusValue);
          
          // Only APPROVED means fully authenticated
          if (authStatusValue === "APPROVED") {
            console.log("✅ User is APPROVED - returning success");
            return responseData;
          } else {
            // Any other status (PENDING, etc) requires authentication
            console.log("⚠️ Auth status is not APPROVED:", authStatusValue, "- throwing 401");
            
            const authError = new Error("Authentication required - status not approved");
            (authError as any).status = 401;
            (authError as any).statusCode = 401;
            (authError as any).response = {
              status: 401,
              data: {
                error: { message: "Authentication required - pending verification" },
                authRequired: true,
                authStatus: authStatusValue
              },
            };
            throw authError;
          }
        }
        
        // Default return for other successful status codes
        return {
          authenticated: true,
          authRequired: false,
          result: responseData?.result || null,
        };
      } catch (error: any) {
        // Check if this is an expected 401 auth required error
        const is401Error =
          error.status === 401 ||
          error.statusCode === 401 ||
          (error.response && error.response.status === 401) ||
          (error.data && error.data.authRequired === true);

        // Also check error message as a fallback
        const isAuthRequiredMessage =
          error.message?.includes("Authentication required") ||
          error.message?.includes("authorization data not found") ||
          error.data?.error?.message?.includes("Authentication required");

        // If this is a 401 or other auth required error
        if (is401Error || isAuthRequiredMessage) {
          // Security: Error handling without logging for security (2025-05-05)- will trigger 2FA modal");

          // Create a properly structured error that the 2FA UI components will recognize
          const authError = new Error("Authentication required");
          (authError as any).status = 401;
          (authError as any).statusCode = 401;
          (authError as any).response = {
            status: 401,
            data: {
              error: { message: "Authentication required" },
              authRequired: true,
            },
          };

          // Return promise rejection with the auth error to trigger 2FA modal
          throw authError;
        }

        // For any other errors, preserve their details but make sure status code is set
        // Security: Error handling without logging for security (2025-05-05)

        // Add statusCode if missing to standardize error format
        if (!error.statusCode && error.response?.status) {
          error.statusCode = error.response.status;
        }

        throw error;
      }
    } catch (error: any) {
      // Add logging here to see what's being thrown from inner try/catch
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const authRequestEmail = async (payload: object): Promise<any | null> => {
    try {
      const response: any = await $fetch(
        `/api/integration/auth-request-email`,
        {
          method: "POST",
          body: payload,
          credentials: "include",
        }
      );

      // Return the full response object so the caller can check status
      return {
        status: 200, // $fetch throws on non-2xx status, so if we're here it's 200
        body: response
      };
    } catch (error: any) {
      // Return error response format
      return {
        status: error?.data?.statusCode || error?.statusCode || 500,
        body: error?.data || { message: error?.data?.message || error?.message || "Failed to send email" }
      };
    }
  };
  const signOutBackend = async (customerId: string): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(`/api/auth/signout`, {
        method: "POST",
        credentials: "include",
        body: { customerId },
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
const register = async (payload: object): Promise<any> => {
  // Maximum number of retries for 500 errors
  const maxRetries = 2;
  let retryCount = 0;
  let lastError: any = null;

  const registerWithRetry = async (): Promise<any> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(`/api/auth/register`, {
        method: "POST",
        body: payload, // payload is from the outer 'register' function scope
        credentials: "include",
        // Set a longer timeout for registration which can be slow
        timeout: 15000, // 15 seconds
        // Don't automatically retry by $fetch itself; our custom logic below handles retries.
        retry: 0,
        onRequest({ options }) {
          // console.log('[AuthService.register] onRequest:', options);
        },
        onRequestError({ error }) {
          console.error("[AuthService.register] Request error during registration:", error);
        },
        onResponse({ response }) {
          // console.log('[AuthService.register] onResponse:', response);
        },
        onResponseError({ response }) {
          console.error(`[AuthService.register] Registration response error: ${response.status}`, response);
        },
      });

      return response; // Return full response on success
    } catch (error: any) {
      console.error("[AuthService.register] Register attempt failed:", error);
      lastError = error;

      const isServerError =
        error.response?.status === 500 ||
        error.message?.includes("Internal Server Error") ||
        error.name === "InternalServerError";

      if (isServerError && retryCount < maxRetries) {
        console.warn(`[AuthService.register] Server error detected. Will retry. Attempt ${retryCount + 1}/${maxRetries}`);
        retryCount++;
        const delay = 1000 * Math.pow(2, retryCount); // e.g., 2s, 4s
        await new Promise((resolve) => setTimeout(resolve, delay));
        return registerWithRetry();
      }

      // Handle structured API errors from Strapi or similar
      if (
        error.response &&
        error.response._data && // Nuxt $fetch specific error structure
        error.response._data.error
      ) {
        const apiError = error.response._data.error;
        // Standardize message if errorMessage is present (common in our backend errors)
        if (apiError.errorMessage && !apiError.message) {
          apiError.message = apiError.errorMessage;
        }
        console.error("[AuthService.register] Throwing structured API error:", apiError);
        throw apiError;
      }

      // For generic server errors after retries or non-retryable server errors
      if (isServerError) {
        const serverError = new Error(
          "The registration service is temporarily unavailable. Please try again in a few moments."
        );
        serverError.name = "TemporaryServerError";
        console.error("[AuthService.register] Throwing temporary server error:", serverError);
        throw serverError;
      }
      
      console.error("[AuthService.register] Throwing original/unhandled error:", error);
      throw error;
    }
  };

  // Call the retry function
  return registerWithRetry();
};

  const forgotPassword = async (
    dateOfBirth: string,
    email: string
  ): Promise<any> => {
    const requestFrom = window.location.origin;

    try {
      const response: any = await $fetch(`/api/integration/forgot-password`, {
        method: "POST",
        body: { dateOfBirth, email, requestFrom },
        credentials: "include",
      });

      // Security: Error handling without logging for security (2025-05-05)
      return response; // Return full response
    } catch (error: any) {
      console.error("forgotPassword failed:", error);

      // Capture and return the error message correctly
      if (
        error.response &&
        error.response._data &&
        error.response._data.error
      ) {
        // Get the error message from the API response
        const apiError = error.response._data.error;
        // Security: Error handling without logging for security (2025-05-05)

        // Check if there's an errorMessage field in the API response
        if (apiError.errorMessage) {
          apiError.message = apiError.errorMessage; // Use errorMessage if available
        }

        throw apiError; // Throw the API error object
      }

      throw error; // If not in the above format, throw the original error
    }
  };

  const validateAuthorizationToken = async (
    token: string
  ): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(`/api/auth/validate-token`, {
        method: "POST",
        credentials: "include",
        body: { token },
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const approveAuthorizationToken = async (
    payload: object
  ): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(`/api/auth/approve-token`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const rejectAuthorizationToken = async (
    payload: object
  ): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(`/api/auth/reject-token`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const authRequestMsn = async (payload: object): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(`/api/auth/request-msn`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      // Return the full response object so the caller can check status
      return {
        status: 200, // $fetch throws on non-2xx status, so if we're here it's 200
        body: response
      };
    } catch (error: any) {
      // Return error response format
      return {
        status: error?.data?.statusCode || error?.statusCode || 500,
        body: error?.data || { message: error?.data?.message || error?.message || "Failed to send SMS" }
      };
    }
  };
  const updatePassword = async (payload: object): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const requestFrom = window.location.origin;
      const response: any = await $fetch(`/api/auth/update-password`, {
        method: "POST",
        body: { ...payload, requestFrom },
        credentials: "include",
      });

      return response?.result || response.error;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
    }
  };
  const registerComplaint = async (payload: object): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(`/api/auth/register-complaint`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const requestOtp = async (payload: object): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(`/api/auth/request-otp`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const pnjRequestOtp = async (payload: object): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(`/api/auth/request-code`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error: any) {
      // Security: Error handling without logging for security (2025-05-05)

      // Capture and return the error message correctly
      if (
        error.response &&
        error.response._data &&
        error.response._data.error
      ) {
        // Get the error message from the API response
        const apiError = error.response._data.error;
        // Security: Error handling without logging for security (2025-05-05)

        // Check if there's an errorMessage field in the API response
        if (apiError.errorMessage) {
          apiError.message = apiError.errorMessage; // Use errorMessage if available
        }

        throw apiError; // Throw the API error object
      }

      throw error; // If not in the above format, throw the original error
    }
  };
  const pnjVerifyOtp = async (payload: object): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(`/api/auth/validate-code`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const pnjReSubmit = async (payload: object): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(`/api/integration/pnj-resubmit`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const verifyOtp = async (payload: object): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(`/api/auth/verify-otp`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const resetPassword = async (payload: object): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(`/api/auth/reset-password`, {
        method: "POST",
        body: payload,
        credentials: "include",
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const verifyToken = async (token: string): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(`/api/auth/verify-token`, {
        method: "POST",
        credentials: "include",
        body: { token },
      });

      return response?.result || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const emailConfirmation = async (token: string): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(`/api/auth/email-confirmation`, {
        method: "POST",
        credentials: "include",
        body: { token },
      });

      // Check for API-level errors even in successful HTTP responses
      if (response?.error) {
        const error = new Error(response.error.message || 'Email confirmation failed');
        throw error;
      }

      return response?.result || response?.data || null;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const emailChange = async (token: string): Promise<any | null> => {
    try {
      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const requestFrom = window.location.origin;
      const response: any = await $fetch(`/api/auth/email-change`, {
        method: "POST",
        credentials: "include",
        body: { token, requestFrom },
      });

      return response?.result || response.error;
    } catch (error) {
      // Security: Error handling without logging for security (2025-05-05)
      throw error;
    }
  };
  const resendEmailConfirmation = async (): Promise<any | null> => {
    try {
      // Get the SITEACTIVEID from cookies
      const cookieStrings = document.cookie.split(";");
      console.log("cookie strings:", cookieStrings);
      
      // Parse cookies into an object
      const cookies = cookieStrings.reduce((acc, cookie) => {
        const [key, value] = cookie.trim().split('=');
        if (key && value) {
          acc[key] = value;
        }
        return acc;
      }, {} as Record<string, string>);
      
      console.log("parsed cookies:", cookies);
      const siteActiveId = getSiteActiveIdFromCookies(cookies);

      // // Find the SITEACTIVEID cookie
      // for (const cookie of cookies) {
      //   const [name, value] = cookie.trim().split("=");
      //   if (name === "SITEACTIVEID") {
      //     siteActiveId = value;
      //     break;
      //   }
      // }

      console.log("Using SITEACTIVEID for email confirmation:", siteActiveId);

      if (!siteActiveId) {
        throw new Error(
          "You must be logged in to resend the confirmation email"
        );
      }

      // SECURITY: Use secure server-side proxy endpoint instead of directly
      // accessing sensitive backend URLs (2025-05-01)
      const response: any = await $fetch(
        `/api/auth/resend-email-confirmation?siteactiveid=${encodeURIComponent(siteActiveId)}`,
        {
          method: "GET",
          credentials: "include",
        }
      );

      return response?.result || null;
    } catch (error: any) {
      // Explicitly convert FetchError to a standard error with the message
      // This ensures components receive clean, user-friendly error messages
      console.error("Email confirmation error:", error.message);

      const errorMessage =
        error.data?.message ||
        error.message ||
        "Could not send email confirmation";

      const customError = new Error(errorMessage);
      customError.name = "EmailConfirmationError";

      throw customError;
    }
  };
  return {
    signInBackend,
    validateEmail,
    authStatus,
    authRequestEmail,
    signOutBackend,
    register,
    forgotPassword,
    validateAuthorizationToken,
    approveAuthorizationToken,
    rejectAuthorizationToken,
    authRequestMsn,
    updatePassword,
    registerComplaint,
    requestOtp,
    pnjRequestOtp,
    pnjVerifyOtp,
    pnjReSubmit,
    resetPassword,
    verifyToken,
    emailConfirmation,
    resendEmailConfirmation,
    emailChange,
  };
};
